import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { DocumentSelectionModal } from '@/components/tasks/task-detail/DocumentSelectionModal';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import { useMatter } from '@/hooks/useMatter';
import { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

import {
  BaseRequestStep,
  BaseRequestStepProps,
  useUpdateRequestStatus,
} from './RequestStatusTrackerStep';

// Custom completion modal for Invoice Ready step
const InvoiceReadyModal = ({
  isOpen,
  onClose,
  request,
  providerId,
}: {
  isOpen: boolean;
  onClose: () => void;
  request: TreatmentRecordRequestSchema;
  providerId: string;
}) => {
  const { matterId } = useMatter();
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedBlobIds, setSelectedBlobIds] = useState<string[]>([]);
  const [invoiceAmount, setInvoiceAmount] = useState<string>('');

  const { changeStatus } = useUpdateRequestStatus({
    requestId: request.id,
    providerId,
  });

  const [createInvoice, { isLoading: isSubmitting }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsInvoicesCreateMutation();

  const { handleUploadToCollection } = useUploadManagerUnified({
    matterId,
    refetch: () => {},
  });

  const handleDocumentSelection = async (blobIds: string[]) => {
    setSelectedBlobIds(blobIds);
    setShowDocumentModal(false);
  };

  const handleSubmitInvoice = async () => {
    try {
      if (selectedBlobIds.length > 0 && invoiceAmount) {
        // Submit invoice if data is provided
        await createInvoice({
          matterId,
          providerId,
          requestId: request.id,
          createTreatmentRecordRequestInvoiceSchema: {
            amount: Number(invoiceAmount),
            date_received: new Date().toISOString(),
            date_paid: new Date().toISOString(),
            invoice_file_id: selectedBlobIds[0],
          },
        });
        toast.success('Invoice submitted successfully');
      }

      // Proceed to next status (either with or without invoice)
      await changeStatus('pending_payment', undefined);
      onClose();
    } catch {
      toast.error('Failed to submit invoice');
    }
  };

  const handleSkip = async () => {
    // Skip invoice upload and just change status
    await changeStatus('pending_payment', undefined);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} size="lg" zIndex={Z_INDEX_MODAL_NESTED} nested>
        <DialogTitle>Submit Invoice</DialogTitle>
        <DialogDescription>
          Upload the invoice document and enter invoice details for this records
          request.
        </DialogDescription>
        <DialogBody>
          <div className="space-y-4">
            <div>
              <Label htmlFor="invoice-amount">Invoice Amount ($)</Label>
              <Input
                id="invoice-amount"
                type="number"
                step="0.01"
                value={invoiceAmount}
                onChange={(e) => setInvoiceAmount(e.target.value)}
                placeholder="0.00"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <Label>Invoice Document</Label>
              <div className="mt-2">
                {selectedBlobIds.length > 0 ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <span className="text-sm text-gray-700">
                      {selectedBlobIds.length} document
                      {selectedBlobIds.length !== 1 ? 's' : ''} selected
                    </span>
                    <Button
                      outline
                      onClick={() => setShowDocumentModal(true)}
                      disabled={isSubmitting}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <Button
                    outline
                    onClick={() => setShowDocumentModal(true)}
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    Select or Upload Invoice
                  </Button>
                )}
              </div>
            </div>

            <DialogActions>
              <Button outline onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button color="zinc" onClick={handleSkip} disabled={isSubmitting}>
                Skip & Continue
              </Button>
              <Button
                color="indigo-steel"
                onClick={handleSubmitInvoice}
                disabled={
                  isSubmitting || !selectedBlobIds.length || !invoiceAmount
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Invoice & Continue'
                )}
              </Button>
            </DialogActions>
          </div>
        </DialogBody>
      </Dialog>

      {showDocumentModal && (
        <DocumentSelectionModal
          open={showDocumentModal}
          onClose={() => setShowDocumentModal(false)}
          matterId={matterId}
          onSelectDocuments={handleDocumentSelection}
          initialSelectedBlobIds={selectedBlobIds}
          singleFile
          onUpload={handleUploadToCollection}
        />
      )}
    </>
  );
};

export const InvoiceReadyStep = ({
  state,
  title,
  nextStatus,
  provider,
  request,
  status,
  actionText,
}: BaseRequestStepProps) => {
  return (
    <BaseRequestStep
      state={state}
      title={title}
      nextStatus={nextStatus}
      provider={provider}
      request={request}
      status={status}
      actionText={actionText || 'Submit Invoice'}
      renderCompletionModal={(props) => (
        <InvoiceReadyModal
          {...props}
          request={request}
          providerId={provider.id}
        />
      )}
    />
  );
};
